import React from "react";
import { AlertTriangle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, testid, eyebrow }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3" data-testid={testid}>
      <div className="border-l-2 border-teal-600 pl-3">
        {eyebrow && (
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-700">{eyebrow}</div>
        )}
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// Crisp enterprise panel — sharp 1px border, minimal rounding, subtle shadow.
export function Panel({ className, children, ...props }) {
  return (
    <div className={cn("rounded-md border border-slate-200 bg-white shadow-sm", className)} {...props}>
      {children}
    </div>
  );
}

export function PanelHeader({ label, icon: Icon, right, className }) {
  return (
    <div className={cn("flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-4 py-2.5", className)}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />} {label}
      </div>
      {right}
    </div>
  );
}

export function SectionLabel({ children, className }) {
  return (
    <div className={cn("text-[10px] font-semibold uppercase tracking-wider text-slate-400", className)}>{children}</div>
  );
}

export function SyntheticBanner({ className }) {
  return (
    <div className={cn("mb-5 flex items-start gap-2.5 rounded-md border-l-4 border-l-amber-500 border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900", className)}
      data-testid="synthetic-banner">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <span>
        <strong>Synthetic demonstration environment.</strong> All patients, providers, payers and
        policies are fictional. Do not enter real patient information. This is not clinical guidance
        and not evidence of coverage or payment.
      </span>
    </div>
  );
}

export function InfoNote({ children, className }) {
  return (
    <div className={cn("flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600", className)}>
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span>{children}</span>
    </div>
  );
}

export function SyntheticChip({ label = "Illustrative / Synthetic" }) {
  return (
    <span className="inline-flex items-center rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
      {label}
    </span>
  );
}
